/**
 * ConversationSurveyCard (TRAC-732)
 *
 * The end-of-conversation survey, mirroring the LivePerson post-conversation
 * survey 1:1: CSAT (1-5) → resolution (yes/no) → free text. Rendered inline at
 * the bottom of the red band when a live-agent chat ends.
 *
 * Progressive: each answer reveals the next question, and each answer is reported
 * up via onSubmit immediately — so a lone CSAT is captured even if the customer
 * stops there. onSubmit is called with the FULL accumulated answers each time
 * (the backend upserts), so the caller never has to merge partials.
 */
import { useEffect, useRef, useState } from 'react';

export interface ConversationSurveyAnswers {
  csat: number;
  resolved?: boolean | null;
  feedback?: string | null;
  /** TRAC-732: which live-chat department this card grades (Sales / Customer Care).
   *  Threaded on every submit so the backend stores + mirrors CSAT per department. */
  department?: string | null;
  /** TRAC-732: the card's step at the time of this answer, so the caller can persist it
   *  on the message part and rehydrate this exact state after a reload. 4 = submitted. */
  step?: number;
}

const RED = '#ce1126';
const CSAT_LABELS = ['Very unhappy', 'Unhappy', 'Neutral', 'Happy', 'Very happy'];

export default function ConversationSurveyCard({
  onSubmit,
  department,
  initial,
}: {
  /** Called after each answered step with the full accumulated answers so far. */
  onSubmit: (answers: ConversationSurveyAnswers) => void;
  /** The department this specific live chat was with (names the CSAT question). */
  department?: string | null;
  /** TRAC-732: previously-persisted answers for THIS card (from the message part), so a
   *  reload rehydrates mid-survey progress — or the locked "thanks" state once submitted —
   *  instead of showing a fresh, re-submittable form. */
  initial?: {
    csat?: number | null;
    resolved?: boolean | null;
    feedback?: string | null;
    step?: number;
  } | null;
}) {
  // Name the team in the question when we know it ("…with Sales?"), else stay generic.
  const teamLabel = (department || '').trim() || 'the agent(s) you interacted with';
  const [csat, setCsat] = useState<number | null>(initial?.csat ?? null);
  const [resolved, setResolved] = useState<boolean | null>(initial?.resolved ?? null);
  const [feedback, setFeedback] = useState(initial?.feedback ?? '');
  // Which question is showing: 1 = CSAT, 2 = resolution, 3 = free text, 4 = done.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(
    ((initial?.step && initial.step >= 1 && initial.step <= 4) ? initial.step : 1) as 1 | 2 | 3 | 4,
  );

  // As each answer reveals the next question, make sure it's comfortably in view —
  // but GENTLY. If at least ~100px of it already shows, leave it alone; otherwise
  // nudge just enough to reveal it clear of the bottom fade (scroll-margin-bottom),
  // never yanking it to the top. We rely ONLY on the room that already exists below
  // the survey (the "Back with the AI tire agent" recap + the wrapper's own bottom
  // padding) — we must NOT mutate the scroll wrapper's padding/height, because its
  // bottom padding is load-bearing for the sticky bottom fade + input-island
  // clearance (mutating it made the fade detach and float mid-interface).
  const REVEAL_PX = 100;
  const q2Ref = useRef<HTMLDivElement>(null);
  const q3Ref = useRef<HTMLDivElement>(null);
  const doneRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const findScroller = (el: HTMLElement | null): HTMLElement | null => {
    let n = el?.parentElement ?? null;
    while (n) {
      const oy = getComputedStyle(n).overflowY;
      if (oy === 'auto' || oy === 'scroll') return n;
      n = n.parentElement;
    }
    return null;
  };

  useEffect(() => {
    if (step <= 1 || step === 4) return;  // nothing new to reveal
    const el = step === 2 ? q2Ref.current : q3Ref.current;
    if (!el) return;
    const sc = scrollerRef.current ?? (scrollerRef.current = findScroller(el));
    if (!sc) return;
    const id = requestAnimationFrame(() => {
      const scRect = sc.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      // Only act if the new step is cut off at the bottom (in the fade) or above the
      // top. If it's already comfortably in view, leave it where it is.
      const belowFold = elRect.bottom > scRect.bottom - REVEAL_PX;
      const aboveTop = elRect.top < scRect.top;
      if (!belowFold && !aboveTop) return;
      // block:'nearest' scrolls the MINIMUM to satisfy the element's scroll-margin —
      // lands it ~REVEAL_PX above the bottom fade without yanking to the top, using
      // only the existing room below.
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [step]);

  const pickCsat = (v: number) => {
    setCsat(v);
    // Re-submit the FULL accumulated answers so changing an earlier answer updates
    // the stored record (upsert) without dropping later ones. Advance to step 2 the
    // first time; if the customer is already further along, keep their place.
    onSubmit({ csat: v, resolved, feedback: feedback.trim() || null, department,
               step: Math.max(step, 2) });
    setStep((s) => (s < 2 ? 2 : s));
  };

  const pickResolved = (v: boolean) => {
    setResolved(v);
    if (csat != null) onSubmit({ csat, resolved: v, feedback: feedback.trim() || null, department,
                                 step: Math.max(step, 3) });
    setStep((s) => (s < 3 ? 3 : s));
  };

  const submitFeedback = () => {
    // Submit = done. Free text is optional (null when empty); csat + resolution were
    // already saved progressively. There's no separate "done" — you submit, or you
    // ignore the survey and leave (what you answered is still kept).
    const text = feedback.trim();
    // step 4 = submitted: persisted so a reload rehydrates the locked "thanks" state and
    // this card can't be answered a second time (one submission per revealed card).
    if (csat != null) onSubmit({ csat, resolved, feedback: text || null, department, step: 4 });
    setStep(4);
  };

  return (
    <>
      {/* Sits in the red band right under the "Chat with X ended" line — OUTSIDE
          the white card — reassuring the customer the survey isn't blocking them. */}
      {step !== 4 && (
        <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.45, margin: '0 0 10px 2px' }}>
          There’s no rush, you can answer the survey below anytime.
        </div>
      )}
      <div style={{ border: '1px solid #d9dfe5', borderRadius: 14, padding: 16, background: '#fff', maxWidth: 420 }}>
        {step === 4 ? (
          <div ref={doneRef} style={{ textAlign: 'center', padding: '6px 0' }}>
            <div style={{ fontWeight: 800, fontSize: 14.5, color: '#1b1d22' }}>Thanks for your feedback!</div>
            <div style={{ fontSize: 12.5, color: '#8a8f98', marginTop: 4 }}>It helps us improve every conversation.</div>
          </div>
        ) : (
          <>
            <div style={{ fontWeight: 800, fontSize: 13, color: RED, letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 14 }}>
              How did the live chat go?
            </div>

          {/* Q1 — CSAT (always visible once answered, shows selection) */}
          <div style={{ marginBottom: step >= 2 ? 14 : 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1b1d22', lineHeight: 1.4, marginBottom: 10 }}>
              How satisfied were you with {teamLabel}?
            </div>
            {/* paddingRight scoots the number values in from the card's right
                edge (which fades into the band) so they never sit near the screen
                edge on narrow phones — the card/text still extend and fade. */}
            <div style={{ display: 'flex', gap: 8, paddingRight: 52 }}>
              {[1, 2, 3, 4, 5].map((v) => {
                const selected = csat === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => pickCsat(v)}
                    aria-label={`${v} — ${CSAT_LABELS[v - 1]}`}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      borderRadius: 10,
                      border: selected ? `2px solid ${RED}` : '1px solid #d9dfe5',
                      background: selected ? RED : '#fff',
                      color: selected ? '#fff' : '#33425a',
                      fontWeight: 800,
                      fontSize: 15,
                      cursor: 'pointer',
                      transition: 'all .12s ease',
                    }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8a8f98', marginTop: 6, paddingRight: 52 }}>
              <span>1 — worst</span>
              <span>5 — best</span>
            </div>
          </div>

          {/* Q2 — Resolution */}
          {step >= 2 && (
            <div ref={q2Ref} style={{ marginBottom: step >= 3 ? 14 : 0, paddingTop: 14, borderTop: '1px solid #f1f3f5', scrollMarginTop: 16, scrollMarginBottom: REVEAL_PX }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1b1d22', lineHeight: 1.4, marginBottom: 10 }}>
                Did {teamLabel} resolve your issue?
              </div>
              <div style={{ display: 'flex', gap: 8, paddingRight: 52 }}>
                {[
                  { label: 'Yes', val: true },
                  { label: 'No', val: false },
                ].map(({ label, val }) => {
                  const selected = resolved === val;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => pickResolved(val)}
                      style={{
                        flex: 1,
                        padding: '10px 0',
                        borderRadius: 999,
                        border: selected ? `2px solid ${RED}` : '1px solid #d9dfe5',
                        background: selected ? RED : '#fff',
                        color: selected ? '#fff' : '#33425a',
                        fontWeight: 700,
                        fontSize: 13.5,
                        cursor: 'pointer',
                        transition: 'all .12s ease',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Q3 — Free text */}
          {step >= 3 && (
            <div ref={q3Ref} style={{ paddingTop: 14, borderTop: '1px solid #f1f3f5', scrollMarginTop: 16, scrollMarginBottom: REVEAL_PX }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1b1d22', lineHeight: 1.4, marginBottom: 10 }}>
                Is there anything we can do to improve this experience?
              </div>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Optional — tell us more"
                rows={3}
                maxLength={2000}
                style={{
                  width: 'calc(100% - 52px)',
                  boxSizing: 'border-box',
                  border: '1px solid #d9dfe5',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 13.5,
                  color: '#1b1d22',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
              <div style={{ display: 'flex', marginTop: 10, paddingRight: 52 }}>
                <button
                  type="button"
                  onClick={submitFeedback}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderRadius: 999,
                    background: RED,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    padding: '11px 15px',
                    cursor: 'pointer',
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </>
  );
}
